import {DBConfig, NgxIndexedDBService} from 'ngx-indexed-db';

import {Inject, Injectable} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {ObINotification, ObNotificationService, WINDOW} from '@oblique/oblique';

import {
  ComplianceRiskVergleichspaare,
  ComplianceRiskVergleichspaareEntry
} from '../../dtos/result/result-m2/compliance-risk-vergleichspaare';
import {DbMitarbeitersData} from './indexedDbData';
import {
  VergleichspaareSortierung,
  mapToSortierung
} from './vergleichspaare-sortierung.enum';
import {ResultDataMitarbeiter} from '../../dtos/x-mitarbeiterBase';
import {ExportDetailErgebnis} from '../../dtos/result/result-m2/export-detail-ergebnis';

const dbName = 'temostaDb';
const mitarbeiterStoreName = 'mitarbeiter';
const mitarbeiterStoreIndex = 1;
const kontrollmodusStoreIndex = 1;
const vergleichspaareStoreName = 'complianceRiskVergleichspaare';
const kontrollmodusMitarbeiterName = 'kontrollmodusMitarbeiter';
const exportDetailErgebnisName = 'exportDetailErgebnis';
const exportDetailErgebnisStoreIndex = 1;

export const temostaDbConfig: DBConfig = {
  name: dbName,
  version: 5,
  objectStoresMeta: [
    {
      store: mitarbeiterStoreName,
      storeConfig: {keyPath: 'storeIndex', autoIncrement: false},
      storeSchema: []
    },
    {
      store: vergleichspaareStoreName,
      storeConfig: {keyPath: 'storeIndex', autoIncrement: false},
      storeSchema: []
    },
    {
      store: kontrollmodusMitarbeiterName,
      storeConfig: {keyPath: 'storeIndex', autoIncrement: false},
      storeSchema: []
    },
    {
      store: exportDetailErgebnisName,
      storeConfig: {keyPath: 'storeIndex', autoIncrement: false},
      storeSchema: []
    }
  ]
};

@Injectable({
  providedIn: 'root'
})
export class IndexedDbStorageService {
  static dbName: string;
  private readonly sortierungMemoryStore: Record<
    string,
    ComplianceRiskVergleichspaareEntry[]
  >;

  constructor(
    private readonly idxDBService: NgxIndexedDBService,
    private readonly translate: TranslateService,
    private readonly notification: ObNotificationService,
    @Inject(WINDOW) private readonly window: Window
  ) {
    this.sortierungMemoryStore = {};
  }

  public async storeMitarbeiter(
    data: DbMitarbeitersData,
    displayWarningIfInacessible = false
  ): Promise<void> {
    return this.storeIdxDbObject(
      mitarbeiterStoreName,
      mitarbeiterStoreIndex,
      data,
      displayWarningIfInacessible
    );
  }

  public async getMitarbeiter(): Promise<DbMitarbeitersData> {
    return this.getIdxDbObject(mitarbeiterStoreName, mitarbeiterStoreIndex);
  }

  public async storeVergleichspaareSortierung(
    sortierung: VergleichspaareSortierung,
    data: ComplianceRiskVergleichspaareEntry[]
  ): Promise<void> {
    try {
      return await this.storeIdxDbObject(
        vergleichspaareStoreName,
        sortierung,
        data
      );
    } catch {
      this.sortierungMemoryStore[sortierung] = data;
    }
    return Promise.resolve();
  }

  public async getVergleichspaareSortierung(
    sortierung: VergleichspaareSortierung
  ): Promise<ComplianceRiskVergleichspaareEntry[]> {
    try {
      return await this.getIdxDbObject(vergleichspaareStoreName, sortierung);
    } catch {
      return this.sortierungMemoryStore[sortierung];
    }
  }

  public async storeAllVergleichspaare(data: ComplianceRiskVergleichspaare) {
    const promises = [] as Promise<void>[];
    for (const key in data) {
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        const vergleichspaareSortierungData = data[key];
        const promise = this.storeVergleichspaareSortierung(
          mapToSortierung(key),
          vergleichspaareSortierungData
        );
        promises.push(promise);
      }
    }
    await Promise.all(promises);
    return Promise.resolve();
  }

  public async getAllVergleichspaare() {
    const result = new ComplianceRiskVergleichspaare();
    for (const key in result) {
      if (Object.prototype.hasOwnProperty.call(result, key)) {
        result[key] = await this.getVergleichspaareSortierung(
          mapToSortierung(key)
        );
      }
    }
    return result;
  }

  public async storeKontrollmodusMitarbeiter(
    data: ResultDataMitarbeiter[]
  ): Promise<void> {
    try {
      return await this.storeIdxDbObject(
        kontrollmodusMitarbeiterName,
        kontrollmodusStoreIndex,
        data
      );
    } catch {
      return Promise.resolve();
    }
  }

  public async getKontrollmodusMitarbeiter(): Promise<ResultDataMitarbeiter[]> {
    try {
      return await this.getIdxDbObject(
        kontrollmodusMitarbeiterName,
        kontrollmodusStoreIndex
      );
    } catch {
      return Promise.resolve([]);
    }
  }

  public async storeExportDetailergebnis(data: ExportDetailErgebnis) {
    try {
      return await this.storeIdxDbObject(
        exportDetailErgebnisName,
        exportDetailErgebnisStoreIndex,
        data
      );
    } catch {
      return Promise.resolve();
    }
  }

  public async getExportDetailergebnis(): Promise<ExportDetailErgebnis> {
    try {
      return await this.getIdxDbObject(
        exportDetailErgebnisName,
        exportDetailErgebnisStoreIndex
      );
    } catch {
      return Promise.resolve(null);
    }
  }

  private async storeIdxDbObject<T>(
    storeName: string,
    storeIndex: number,
    data: T,
    displayWarning = false
  ): Promise<void> {
    try {
      if (await this.indexedDBAvailable()) {
        await this.idxDBService
          .update(storeName, Object.assign(data, {storeIndex}))
          .toPromise();
        return await Promise.resolve();
      }
    } catch (error: unknown) {
      // eslint-disable-next-line no-console
      console.debug('IndexedDB not available (retry later)', error);
    }
    if (displayWarning) {
      this.notSupportedIndexDbNotification();
    }
  }

  private async getIdxDbObject<T>(
    storeName: string,
    storeIndex: number
  ): Promise<T> {
    try {
      const storeHasValues = await this.storeHasValues(storeName, storeIndex);
      if (storeHasValues) {
        return await this.idxDBService
          .getByKey<T>(storeName, storeIndex)
          .toPromise();
      }
      console.warn('IndexedDB empty or inaccessible');
    } catch (e: unknown) {
      console.error('Unexpected getIdxDbObject');
    }
    return Promise.reject(
      new Error('failed to load mitarbeiter from IndexedDB')
    );
  }

  private indexedDBAvailable(): Promise<boolean> {
    return new Promise(resolve => {
      const connectionRequest = this.window.indexedDB.open(dbName);
      connectionRequest.onerror = () => resolve(false);
      connectionRequest.onsuccess = () => resolve(true);
    });
  }

  private async storeHasValues(
    storeName: string,
    storeIndex: number
  ): Promise<boolean> {
    if (await this.indexedDBAvailable()) {
      return (
        (await this.idxDBService.count(storeName, storeIndex).toPromise()) > 0
      );
    }
    return false;
  }

  private async notSupportedIndexDbNotification() {
    const msg = await this.translate
      .get('i18n.analyzer.upload.warning.noIndexedDb')
      .toPromise();
    this.notification.warning({
      message: msg,
      sticky: true
    } as ObINotification);
  }
}
